import { LinkPreview } from '@atoms';
import { DATE_FORMAT } from '@constants';
import { removedCellClassName } from '@helpers';
import { Box, Link, Tooltip, Typography } from '@mui/material';
import { GridColDef, GridValueGetterParams } from '@mui/x-data-grid';
import ColumnFilter from '@organisms/Enterprise/LinksManagement/ColumnFilter/ColumnFilter';
import LinkManagementStatus from '@organisms/Enterprise/LinksManagement/LinkManagementStatus/LinkManagementStatus';
import dayjs from 'dayjs';
import * as yup from 'yup';
import {
  EIdentifiedTypeLinkManagement,
  EIdentifiedTypeLinkManagementValue,
  ELinkPriorityValues,
  EPriorityLinkManagement,
  EPriorityLinkManagementValue,
  EStatusLinkManagement,
  EStatusLinkManagementValue,
} from './enums';
import { ITableLinksManagement } from './types';
import { FilterItem } from '@services';
import { ReactNode } from 'react';
import { renderDateFilter } from '@molecules/Table/filters';
import LinkManagementPrioritySelect from '@organisms/Enterprise/LinksManagement/LinkManagementPrioritySelect/LinkManagementPrioritySelect';
import LinkManagementSourceTypeSelect from '@organisms/Enterprise/LinksManagement/LinkManagementSourceTypeSelect/LinkManagementSourceTypeSelect';
import { LinkManagementCategory, LinkManagementCategoryMapper } from './types';

export const editEnterpriseLinksManagementInputSchema = yup.object({
  status: yup.string().required('Status is required'),
});

export const getColumns = (): GridColDef<ITableLinksManagement>[] => {
  return [
    {
      field: 'url',
      headerName: 'Link',
      minWidth: 350,
      flex: 1,
      cellClassName: removedCellClassName,
      valueGetter: (params: GridValueGetterParams) => params.row.url,
      renderCell: ({ value }) => (
        <Box display="flex" alignItems="center" sx={{ width: '100%' }}>
          <Tooltip title={value}>
            <Link
              className="ellipsis"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
                width: '90%',
              }}
              href={value}
              target="_blank"
            >
              {value}
            </Link>
          </Tooltip>
          <LinkPreview isImage={false} src={value} />
        </Box>
      ),
      filterable: false,
      sortable: false,
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 100,
      maxWidth: 300,
      flex: 1,
      cellClassName: removedCellClassName,
      valueGetter: (params: GridValueGetterParams) => params.row.status,
      renderHeader: (params) => (
        <Box
          display="flex"
          alignItems="center"
          fontWeight={700}
          gap={1}
          onClick={(e) => e.stopPropagation()}
        >
          <span>{params.colDef.headerName}</span>
          <ColumnFilter paramKey="status" />
        </Box>
      ),
      renderCell: ({ value }) => (
        <>
          <LinkManagementStatus status={value} />
        </>
      ),
      resizable: false,
      sortable: false,
      filterable: false,
    },
    {
      field: 'priority',
      headerName: 'Priority',
      minWidth: 100,
      maxWidth: 150,
      flex: 0.7,
      cellClassName: removedCellClassName,
      valueGetter: (params: GridValueGetterParams) => params.row.priority,
      renderCell: ({ value }) => {
        const convertedValue = getLinksPriorityRange(+value);
        if (!convertedValue) return '';

        const config = getConfigPriorityLinkManagement(convertedValue);
        return (
          <Typography
            sx={{
              color: config?.color,
            }}
          >
            {config?.priority}
          </Typography>
        );
      },
      resizable: false,
      sortable: false,
      filterable: false,
    },
    {
      field: 'identifiedType',
      headerName: 'Identified type',
      minWidth: 120,
      maxWidth: 300,
      flex: 1,
      headerAlign: 'left',
      sortable: false,
      align: 'left',
      renderCell: ({ value }) => {
        const config = getConfigIdentifiedTypeLinkManagement(value);
        return <Typography>{config?.identifiedType}</Typography>;
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Update date',
      minWidth: 140,
      flex: 0.8,
      cellClassName: removedCellClassName,
      valueGetter: (params: GridValueGetterParams) => params.row.updatedAt,
      renderCell: ({ value }) => (
        <Box>
          <p>{value ? dayjs(value).format(DATE_FORMAT) : 'N/A'}</p>
        </Box>
      ),
      filterable: false,
      sortable: false,
    },
    {
      field: 'createdAt',
      headerName: 'Date of addition',
      minWidth: 140,
      flex: 0.8,
      cellClassName: removedCellClassName,
      valueGetter: (params: GridValueGetterParams) => params.row.createdAt,
      renderCell: ({ value }) => (
        <Box>
          <p>{value ? dayjs(value).format(DATE_FORMAT) : 'N/A'}</p>
        </Box>
      ),
      filterable: false,
      sortable: false,
    },
  ];
};

export const getConfigCategoryLinkManagement = (category: string) => {
  let value = '';
  switch (category) {
    case '0':
      value = LinkManagementCategory.CyberLockers;
      break;
    case '1':
      value = LinkManagementCategory.StreamOnDemand;
      break;
    case '2':
      value = LinkManagementCategory.LiveStreams;
      break;
    case '3':
      value = LinkManagementCategory.Torrents;
      break;
    case '4':
      value = LinkManagementCategory.SocialMedia;
      break;
    case '5':
      value = LinkManagementCategory.ImpersonationViolation;
      break;
    case '6':
      value = LinkManagementCategory.PiracyViolation;
      break;
    case '7':
      value = LinkManagementCategory.DeepFakeIdentified;
      break;
    case '8':
      value = LinkManagementCategory.Counterfeit;
      break;
    case '9':
      value = LinkManagementCategory.CopyrightViolation;
      break;
    case '10':
      value = LinkManagementCategory.TrademarkViolation;
      break;
    case '11':
      value = LinkManagementCategory.DesignViolation;
      break;
    case '12':
      value = LinkManagementCategory.PolicyViolation;
      break;
    case '13':
      value = LinkManagementCategory.FalsePositive;
      break;
    default:
      value = '';
      break;
  }

  return LinkManagementCategoryMapper[value as LinkManagementCategory];
};

export const getConfigStatusLinkManagement = (status: EStatusLinkManagement) => {
  switch (status.toString()) {
    case EStatusLinkManagement.Detection:
      return {
        color: '#E53935',
        backgroundColor: '#FADBD8',
        filterColor: '#E53935',
        status: EStatusLinkManagementValue.Detection,
      };
    case EStatusLinkManagement.ClientReview:
      return {
        color: '#802819',
        backgroundColor: '#FFDED4',
        filterColor: '#FF7043',
        status: EStatusLinkManagementValue.ClientReview,
      };
    case EStatusLinkManagement.Enforcement:
      return {
        color: '#1A3A71',
        backgroundColor: '#D4E1FB',
        filterColor: '#3369E8',
        status: EStatusLinkManagementValue.Enforcement,
      };
    case EStatusLinkManagement.Resolved:
      return {
        color: '#005F2A',
        backgroundColor: '#C3E7CD',
        filterColor: '#29B15E',
        status: EStatusLinkManagementValue.Resolved,
      };
    case EStatusLinkManagement.Archived:
      return {
        color: '#607D8B',
        backgroundColor: '#F5F7F8',
        filterColor: '#9DA9B1',
        status: EStatusLinkManagementValue.Archived,
      };
    case EStatusLinkManagement.Pending:
      return {
        color: '#607D8B',
        backgroundColor: '#F5F7F8',
        filterColor: '#9DA9B1',
        status: EStatusLinkManagementValue.Pending,
      };
  }
};

export const getConfigPriorityLinkManagement = (priority: EPriorityLinkManagement) => {
  switch (priority.toString()) {
    case EPriorityLinkManagement.High:
      return {
        color: '#E53935',
        priority: EPriorityLinkManagementValue.High,
      };
    case EPriorityLinkManagement.Medium:
      return {
        color: '#FF7043',
        priority: EPriorityLinkManagementValue.Medium,
      };
    case EPriorityLinkManagement.Low:
      return {
        color: '#546E7A',
        priority: EPriorityLinkManagementValue.Low,
      };
    case EPriorityLinkManagement.NoActionNeeded:
      return {
        color: '#9DA9B1',
        priority: EPriorityLinkManagementValue.NoActionNeeded,
      };
  }
};

export const FILTERS_LIST: FilterItem[] = [
  {
    name: 'Priority',
    key: 'priority',
    getFilter: (_, onClose, setFilters, filter): ReactNode => (
      <LinkManagementPrioritySelect
        key="priority"
        name="priority"
        savedDataSelectedRow={{}}
        setSavedDataSelectedRow={({ priority }) => setFilters({ ...filter, priority })}
        handleClose={onClose}
      />
    ),
  },
  {
    name: 'Identified Type',
    key: 'identifiedType',
    getFilter: (_, onClose, setFilters, filter): ReactNode => (
      <LinkManagementSourceTypeSelect
        key="identifiedType"
        name="identifiedType"
        savedDataSelectedRow={{}}
        setSavedDataSelectedRow={({ identifiedType }) => setFilters({ ...filter, identifiedType })}
        handleClose={onClose}
      />
    ),
  },
  {
    name: 'Period',
    key: 'lastModified',
    resetFields: ['createdFrom', 'createdTo'],
    getFilter: renderDateFilter({
      label: 'Period',
      key: 'lastModified',
      resetFields: ['createdFrom', 'createdTo'],
    }),
  },
];

export const getConfigIdentifiedTypeLinkManagement = (
  identifiedType: EIdentifiedTypeLinkManagement,
) => {
  switch (identifiedType.toString()) {
    case EIdentifiedTypeLinkManagement.AtSource:
      return {
        identifiedType: EIdentifiedTypeLinkManagementValue.AtSource,
      };
    case EIdentifiedTypeLinkManagement.SearchEngine:
      return {
        identifiedType: EIdentifiedTypeLinkManagementValue.SearchEngine,
      };
    case EIdentifiedTypeLinkManagement.SubmittedManually:
      return {
        identifiedType: EIdentifiedTypeLinkManagementValue.SubmittedManually,
      };
    default:
      break;
  }
};

// High Priority (0.00 – 0.50): Needs immediate attention
// Medium Priority (0.51 – 0.65): Review when possible
// Low Priority (0.66 – 1.00): Likely low-risk or less urgent

export const getLinksPriorityRange = (priority: number): EPriorityLinkManagement | null => {
  switch (true) {
    case priority >= ELinkPriorityValues.HIGH_MIN && priority <= ELinkPriorityValues.HIGH_MAX:
      return EPriorityLinkManagement.High;
    case priority >= ELinkPriorityValues.MEDIUM_MIN && priority <= ELinkPriorityValues.MEDIUM_MAX:
      return EPriorityLinkManagement.Medium;
    case priority >= ELinkPriorityValues.LOW_MIN && priority <= ELinkPriorityValues.LOW_MAX:
      ELinkPriorityValues;
      return EPriorityLinkManagement.Low;
    case priority === ELinkPriorityValues.NoActionNeeded:
      return EPriorityLinkManagement.NoActionNeeded;
    default:
      return null;
  }
};
